import React, {useEffect} from "react";
import {
  browserLocalPersistence,
  isSignInWithEmailLink,
  parseActionCodeURL,
  setPersistence,
  signInWithEmailLink,
  updateProfile
} from "firebase/auth";
import {auth, ga} from "@web/lib/firebase";
import {Alert, Button, Divider, Input, Space, Typography} from "antd";
import useLocalStorage from "@web/hooks/useLocalStorage";
import {useLocation} from "wouter";
import {notifyAndTrack} from "@web/components/notification";
import {MailOutlined} from "@ant-design/icons";
import useQuerystring from "@web/hooks/useQuerystring";
import {colls, setDocument} from "@web/lib/firestore.db";
import now from "lodash/now.js";
import AuthLayoutWrapper from "@web/components/AuthLayoutWrapper";
import {useAuthOven} from "@web/hooks/useAuthOven";
import Loading from "@web/components/Loading";

const getFirebaseErrorMessage = (code) => {
  switch (code) {
    case "auth/email-already-in-use":
      return "Email is Already In Use";
    case "auth/expired-action-code":
      return "The link has expired.  Make sure you used the latest link sent to your email.";
    case "auth/invalid-action-code":
      return "The link is not valid. Make sure you used the latest link sent to your email.";
    case "auth/weak-password":
      return "Weak Password";
    case "auth/invalid-email":
      return "Invalid Email";
    case "auth/user-not-found":
      return "User Not Found";
    case "auth/wrong-password":
      return "Wrong Password";
    default:
      return "Something went wrong. Try again later.";
  }
};
const setAuthPersistence = () => setPersistence(auth, browserLocalPersistence);
// const syncAnonContent = (user, anonUserId) => {
//   setDocument(colls["user-profile"], userId, {anonUserId}, true).then();
// };
const fullWidthStyle = {width: "100%"};

function VerifyEmailLink() {
  const [user] = useAuthOven(auth);
  const isSigningInWithEmailLink = isSignInWithEmailLink(auth, window.location.href);
  const [requestEmail, setRequestEmail] = React.useState(false);
  const qs = useQuerystring();
  const [emailLS, setEmailLS] = useLocalStorage("signup-email");
  const [email, setEmail] = React.useState("");
  const [anonUserId, setAnonUID] = useLocalStorage("anon-user-id");
  const [error, setError] = React.useState(null);
  const [retry, setRetry] = React.useState(0);
  const [authComplete, setAuthComplete] = React.useState(false);
  const [, setLocation] = useLocation();

  const doRedirect = () => {
    setEmailLS("");
    setAnonUID("");
    const {redirectTo = "/"} = qs;
    setLocation(redirectTo);
  }

  React.useEffect(() => {
    const cachedEmail = email || emailLS;
    console.log("cachedEmail", cachedEmail);

    if (!isSigningInWithEmailLink) {
      setError({code: ""});
      return;
    }

    if (!cachedEmail) {
      setRequestEmail(true);
      return;
    }
    const actionCodeUrl = parseActionCodeURL(window.location.href);

    if (actionCodeUrl.tenantId) {
      auth.tenantId = actionCodeUrl.tenantId;
    }

    if (user && user.emailVerified) {
      return setAuthComplete(true);
    }
    console.log(user);

    setAuthPersistence()
      .then(() => signInWithEmailLink(auth, cachedEmail, window.location.href))
      .then((result) => {
        console.log("Email Link Verified result:", result);
        if (!result.user) {
          return notifyAndTrack.error("Please try again.");
        }
        if (result.additionalUserInfo?.isNewUser) {
          ga.setUserId(result.userId);
          setAuthComplete(true);
          !user.displayName &&
          updateProfile(result.user, {displayName: email.split("@")[0]})
          .catch(error => {
            notifyAndTrack.error("Unable to complete verification");
            setError(error);
          });
        } else setAuthComplete(true);
      })
      .catch((error) => {
        console.log("Error verifying link", error.code);
        setError(error);
      });
  }, [retry]);

  const errorMessage = error && getFirebaseErrorMessage(error.code);

  useEffect(() => {
    if (authComplete && user) doRedirect();
  }, [authComplete, user]);

  if (user && user.emailVerified) {
    return null;
  }

  if (!error && !requestEmail) {
    return <Loading />;
  }

  return <AuthLayoutWrapper hideLinks>
    <div className="flex-col middle center" style={fullWidthStyle}>
      <Typography.Title level={4}>Finish Verification</Typography.Title>
      <div className="flex-col middle center">
        <Divider/>
        {!error && requestEmail &&
          <Space style={fullWidthStyle}>
            <Input
              size="large"
              prefix={<MailOutlined/>}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
            />
            <Button size="large" type="primary" onClick={() => setRetry(now())}>{qs.cta || "Continue"}</Button>
          </Space>
        }
        {!!error &&
          <Space direction="vertical">
            <Alert type="warning" description={<span>{errorMessage}</span>} showIcon/>
            <Typography.Link href="/">Home</Typography.Link>
          </Space>
        }
      </div>
    </div>
  </AuthLayoutWrapper>;
}

export default VerifyEmailLink;
